// src/components/Popups/Settings/DistrictsSetting.jsx
//
// Setări (doar admin) → Sectoare și poligoane: sectoarele școlii și unde e poligon. Elevii dintr-un
// sector fără poligon fac primele lecții pe poligon; cu mai multe poligoane
// elevul alege o dată. Numele nu se schimbă după creare (lecțiile, instructorii
// și grupele păstrează sectorul ca text) — un sector nefolosit se dezactivează.

import React, { useCallback, useEffect, useState } from "react";

import {
   createDistrict,
   getDistricts,
   updateDistrict,
} from "../../../api/districtsService";
import { reloadDistricts } from "../../../hooks/useDistricts";
import { ButtonUI, InputUI, ListUI, StatusUI, ToggleUI } from "../../UI";

const NO_POLYGON_WARNING =
   "Nu va mai rămâne niciun poligon activ: elevii noi nu vor mai face lecții pe poligon. Continui?";

export default function DistrictsSetting() {
   const [districts, setDistricts] = useState(null);
   const [busyId, setBusyId] = useState(null);
   const [newName, setNewName] = useState("");
   const [newHasPolygon, setNewHasPolygon] = useState(false);
   const [feedback, setFeedback] = useState(null);

   const load = useCallback(() => {
      getDistricts()
         .then((list) => setDistricts(Array.isArray(list) ? list : []))
         .catch((err) => setFeedback({ ok: false, text: err?.message }));
   }, []);

   useEffect(() => {
      load();
   }, [load]);

   const activePolygons = (districts || []).filter((d) => d.isActive && d.hasPolygon);

   const change = async (district, patch) => {
      const losesPolygon =
         district.isActive &&
         district.hasPolygon &&
         (patch.hasPolygon === false || patch.isActive === false);
      if (losesPolygon && activePolygons.length === 1 && !window.confirm(NO_POLYGON_WARNING)) {
         return;
      }
      setBusyId(district.id);
      setFeedback(null);
      try {
         const updated = await updateDistrict(district.id, patch);
         setDistricts((list) => list.map((d) => (d.id === district.id ? updated : d)));
         reloadDistricts().catch(() => {});
      } catch (err) {
         setFeedback({ ok: false, text: err?.message });
      } finally {
         setBusyId(null);
      }
   };

   const add = async (event) => {
      event.preventDefault();
      const name = newName.trim();
      if (!name) return;
      setBusyId("new");
      setFeedback(null);
      try {
         const created = await createDistrict({ name, hasPolygon: newHasPolygon });
         setDistricts((list) => [...(list || []), created]);
         setNewName("");
         setNewHasPolygon(false);
         setFeedback({ ok: true, text: `Sectorul „${created.name}” a fost adăugat.` });
         reloadDistricts().catch(() => {});
      } catch (err) {
         setFeedback({ ok: false, text: err?.message });
      } finally {
         setBusyId(null);
      }
   };

   return (
      <div className="appearancePopup__stack">
         {!districts && !feedback && <StatusUI tone="loading" />}

         {districts && (
            <ListUI as="ul">
               {districts.map((d) => (
                  <ListUI.Item
                     key={d.id}
                     title={d.name}
                     detail={d.isActive ? null : "Dezactivat"}
                     aside={
                        <span className="appearancePopup__toggles">
                           <ToggleUI
                              look="checkbox"
                              label="Poligon"
                              checked={Boolean(d.hasPolygon)}
                              disabled={busyId != null}
                              onChange={(next) => change(d, { hasPolygon: next })}
                           />
                           <ToggleUI
                              look="checkbox"
                              label="Activ"
                              checked={Boolean(d.isActive)}
                              disabled={busyId != null}
                              onChange={(next) => change(d, { isActive: next })}
                           />
                        </span>
                     }
                  />
               ))}
            </ListUI>
         )}

         {districts && (
            <form className="appearancePopup__addRow" onSubmit={add}>
               <InputUI
                  label="Sector nou"
                  maxLength={60}
                  value={newName}
                  disabled={busyId != null}
                  onChange={(e) => setNewName(e.target.value)}
                  className="appearancePopup__grow"
               />
               <ToggleUI
                  look="checkbox"
                  label="Poligon"
                  checked={newHasPolygon}
                  disabled={busyId != null}
                  onChange={(next) => setNewHasPolygon(next)}
               />
               <ButtonUI
                  type="submit"
                  variant="primary"
                  loading={busyId === "new"}
                  disabled={busyId != null || newName.trim().length < 2}
               >
                  Adaugă
               </ButtonUI>
            </form>
         )}

         {feedback?.text && (
            <StatusUI tone={feedback.ok ? "success" : "error"}>{feedback.text}</StatusUI>
         )}
      </div>
   );
}
